import * as React from 'react';
import {Minus, Plus} from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {cn} from '../lib/cn';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({className, ...props}, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      'accordion__item',
      'rounded-[var(--accordion-item-radius)] border border-solid border-[var(--accordion-item-border)] bg-[var(--accordion-item-bg)] [box-shadow:var(--accordion-item-shadow)] transition-colors duration-200 ease-in-out [border-top-color:var(--accordion-item-divider)] data-[state=open]:bg-[var(--accordion-item-open-bg)] data-[state=closed]:bg-[var(--accordion-item-bg)] data-[state=closed]:hover:bg-[var(--accordion-item-hover-bg)]',
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

export type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> & {
  openIcon?: React.ReactNode;
  closedIcon?: React.ReactNode;
};

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({className, children, openIcon, closedIcon, ...props}, ref) => {
  const resolvedClosedIcon = closedIcon ?? (
    <Plus aria-hidden="true" className="h-4 w-4" />
  );
  const resolvedOpenIcon = openIcon ?? <Minus aria-hidden="true" className="h-4 w-4" />;

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'accordion__trigger',
          'flex h-auto w-full flex-1 items-center justify-between text-left outline-none transition-colors duration-200',
          'hover:no-underline disabled:pointer-events-none',
          'gap-[var(--accordion-trigger-gap)] p-6 text-lg leading-7 text-[var(--accordion-trigger-color)] [font-family:var(--accordion-trigger-font-family)] [font-weight:var(--accordion-trigger-font-weight)] focus-visible:shadow-[var(--accordion-trigger-focus-shadow)] disabled:opacity-[var(--accordion-trigger-disabled-opacity)]',
          'group',
          className,
        )}
        {...props}
      >
        <span className="flex-1 text-left">{children}</span>
        <div
          className={cn(
            'accordion__icon',
            'ml-auto h-[var(--accordion-icon-size)] w-4 min-w-0 shrink-0 rounded-full p-0.5 text-[var(--accordion-icon-color-closed)] group-data-[state=open]:text-[var(--accordion-icon-color-open)] [&_svg]:h-[var(--accordion-icon-size)] [&_svg]:w-[var(--accordion-icon-size)] [&_svg]:transition-transform [&_svg]:duration-200',
            'transition-colors duration-200',
            'relative',
          )}
        >
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-300',
              'group-data-[state=open]:opacity-0',
            )}
            aria-hidden="true"
          >
            {resolvedClosedIcon}
          </span>
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-300',
              'opacity-0 group-data-[state=open]:opacity-100',
            )}
            aria-hidden="true"
          >
            {resolvedOpenIcon}
          </span>
        </div>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

export interface AccordionContentProps extends React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Content
> {
  contentClassName?: string;
}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({className, contentClassName, children, ...props}, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'accordion__content',
      'transition-all',
      'data-[state=closed]:animate-accordion-up',
      'data-[state=open]:animate-accordion-down',
      'text-[length:var(--accordion-content-font-size)] leading-[var(--accordion-content-line-height)] text-[var(--accordion-content-color)]',
      // TODO Uncomment the next line when dropdown position is fixed
      // 'overflow-hidden',
      className,
    )}
    {...props}
  >
    <div className={cn('accordion__contentItem px-6 pb-6 pt-2', contentClassName)}>
      {children}
    </div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {Accordion, AccordionContent, AccordionItem, AccordionTrigger};
